import Axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardGroup, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import config from '../../../../config';

const formatCurrency = (value) => Number(value || 0).toLocaleString('pt-br', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('pt-br') : '-');

export default function EmpenhoModal({ identificador, id, onHide }) {
  const [empenhoData, setEmpenhoData] = useState(null);
  const [loading, setLoading] = useState(false);

  const getEmpenhoData = useCallback(async () => {
    setLoading(true);

    try {
      if (id && identificador) {
        const res = await Axios.get(`${config.api_path}/public/v1/transparencia/despesa/${identificador}/empenho/${id}`);

        if (!res.data.error) {
          setEmpenhoData(res.data.data);
        }
      } else {
        setEmpenhoData(null);
      }
    } catch (err) {
      console.error(err);
      setEmpenhoData(null);
    } finally {
      setLoading(false);
    }
  }, [identificador, id]);

  useEffect(() => {
    getEmpenhoData();
  }, [getEmpenhoData]);

  const valorAtualizado = useMemo(() => {
    if (!empenhoData) return 0;

    const valorElaborado = empenhoData.empenho.valor || 0;
    const valorAnulado = empenhoData.anulacoes.reduce((acc, anulacao) => acc + (anulacao.valor || 0), 0);
    const valorReforcado = empenhoData.reforcos.reduce((acc, reforco) => acc + (reforco.valor || 0), 0);

    return valorElaborado - valorAnulado + valorReforcado;
  }, [empenhoData]);

  const licitacao = empenhoData &&
    empenhoData.empenho &&
    empenhoData.empenho.contrato &&
    empenhoData.empenho.contrato.licitacao;

  return (
    <Modal show={!!id} onHide={onHide} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title className='w-100 d-flex justify-content-between'>
          <span>Empenho</span>
          <span>{empenhoData ? `Nº ${empenhoData.empenho.numero}/000` : id}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className='d-flex justify-content-center align-items-center'>
            <div>
              <FontAwesomeIcon icon='cog' spin /> Buscando dados do Empenho...
            </div>
          </div>
        )}

        {!loading && empenhoData && (
          <div>
            <Card className='mb-2'>
              <Card.Body>
                <Card.Title>Informações do Empenho</Card.Title>
                <div className='mb-2 emenda-empenho-info-grid'>
                  <strong>Tipo</strong>
                  <span>{empenhoData.empenho.empenhoTipo.nome}</span>

                  <strong>Data de Elaboração</strong>
                  <span>{formatDate(empenhoData.empenho.dataElaboracao)}</span>

                  <strong>Valor</strong>
                  <span>R$ {formatCurrency(empenhoData.empenho.valor)}</span>

                  <OverlayTrigger overlay={<Tooltip>Valor do Empenho - Anulações + Reforços</Tooltip>}>
                    <strong>
                      Valor Atualizado<sup style={{ cursor: 'pointer' }}>?</sup>
                    </strong>
                  </OverlayTrigger>
                  <span>R$ {formatCurrency(valorAtualizado)}</span>
                </div>
                <div>
                  <strong>Histórico</strong>
                  <p className='emenda-empenho-historico'>{empenhoData.empenho.historico}</p>
                </div>
              </Card.Body>
            </Card>

            <Card className='mb-2'>
              <Card.Body>
                <Card.Title>Fornecedor</Card.Title>
                <div className='mb-2 emenda-empenho-info-grid'>
                  <strong>Documento</strong>
                  <span>{empenhoData.empenho.fornecedor.documento}</span>

                  <strong>Nome</strong>
                  <span>{empenhoData.empenho.fornecedor.dadosFornecedor.nome}</span>
                </div>
              </Card.Body>
            </Card>

            {licitacao && licitacao.id && (
              <Card className='mb-2'>
                <Card.Body>
                  <Card.Title>Licitação</Card.Title>
                  <div className='mb-2 emenda-empenho-info-grid'>
                    <strong>{licitacao.modalidadeLicitacao.nome}</strong>
                    <span>
                      N° {licitacao.numeroPorModalidade}/
                      {licitacao.exercicio}
                    </span>

                    <strong>Número do Processo</strong>
                    <span>
                      N° {licitacao.numeroProcesso}/
                      {licitacao.exercicio}
                    </span>

                    <strong>Data de Homologação</strong>
                    <span>{formatDate(licitacao.dataHomologacao)}</span>

                    <strong>Valor</strong>
                    <span>R$ {formatCurrency(licitacao.valor)}</span>
                  </div>
                  <div className='mb-2'>
                    <strong>Objeto</strong>
                    <div>
                      {licitacao.objeto}
                    </div>
                  </div>
                  <div className='mb-2'>
                    <a href={`${licitacao.urlPortal}`} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon="search" /> Detalhes
                    </a>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Card className='mb-2'>
              <Card.Body>
                <Card.Title>Dotação {empenhoData.empenho.dotacao.codigo}</Card.Title>
                <div className='mb-2 emenda-empenho-dotacao-grid'>
                  <strong>Unidade Orçamentária</strong>
                  <span>
                    {empenhoData.empenho.dotacao.acaoGovernamental.unidadeOrcamentaria.codigo} -{' '}
                    {empenhoData.empenho.dotacao.acaoGovernamental.unidadeOrcamentaria.nome}
                  </span>

                  <strong>Ação Governamental</strong>
                  <span>
                    {empenhoData.empenho.dotacao.acaoGovernamental.codigo} -{' '}
                    {empenhoData.empenho.dotacao.acaoGovernamental.nome}
                  </span>

                  <strong>Despesa Orçamentária</strong>
                  <span>
                    {empenhoData.empenho.dotacao.despesaOrcamentaria.codigo} -{' '}
                    {empenhoData.empenho.dotacao.despesaOrcamentaria.nome}
                  </span>

                  <strong>Fonte de Recurso</strong>
                  <span>
                    {empenhoData.empenho.dotacao.fonteRecurso.codigo} - {empenhoData.empenho.dotacao.fonteRecurso.nome}
                  </span>
                </div>
              </Card.Body>
            </Card>

            <CardGroup>
              <Card>
                <Card.Body className='px-0'>
                  <Card.Title className='px-4'>Liquidações</Card.Title>
                  <Table size='sm'>
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empenhoData.liquidacoes.map((liquidacao, index) => (
                        <tr key={index}>
                          <td>{formatDate(liquidacao.dataLiquidacao)}</td>
                          <td className='text-right'>{formatCurrency(liquidacao.valor)}</td>
                        </tr>
                      ))}
                      {empenhoData.liquidacoes.length === 0 && (
                        <tr>
                          <td colSpan={2} className='text-center text-muted'>Nenhuma liquidação registrada</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <td className='text-right'>
                          {formatCurrency(empenhoData.liquidacoes.reduce((acc, liquidacao) => acc + (liquidacao.valor || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card.Body>
              </Card>

              <Card>
                <Card.Body className='px-0'>
                  <Card.Title className='px-4'>Pagamentos</Card.Title>
                  <Table size='sm'>
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empenhoData.pagamentos.map((pagamento, index) => (
                        <tr key={index}>
                          <td>{formatDate(pagamento.pagamento.dataPagamento)}</td>
                          <td className='text-right'>{formatCurrency(pagamento.valor)}</td>
                        </tr>
                      ))}
                      {empenhoData.pagamentos.length === 0 && (
                        <tr>
                          <td colSpan={2} className='text-center text-muted'>Nenhum pagamento registrado</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <td className='text-right'>
                          {formatCurrency(empenhoData.pagamentos.reduce((acc, pagamento) => acc + (pagamento.valor || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card.Body>
              </Card>
            </CardGroup>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
